'use client';

import React, { useEffect, useMemo } from 'react';
import { IconSearch } from '@repo/svgs';
import { TabNavigation } from './TabNavigation';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { BuildingsSearchBarMobile } from '@/features/buildings/BuildingsSearchBar.Mobile';
import { LogoLink } from '@/components/shared/LogoLink';
import { PersonalizePill } from './PersonalizePill';
import { HomeSearchMobile } from './HomeSearchMobile';
import { AiMiniSearchBar } from '@/features/ai-search/AiMiniSearchBar';
import { useAiSearchStatus } from '@/features/ai-search/hooks/useAiSearchStatus';

interface TopBarMobileProps {
  h1: string;
}

export function TopBarMobile({ h1 }: TopBarMobileProps) {
  const [isCondensed, setIsCondensed] = React.useState(false);
  const [aiSearchOpen, setAiSearchOpen] = React.useState(false);
  const pathname = usePathname();
  const isReviews = pathname === '/read-reviews';
  const aiStatus = useAiSearchStatus();
  const aiEnabled = !isReviews && aiStatus !== 'disabled';
  const searchSectionRef = React.useRef<HTMLDivElement>(null);
  const headerRef = React.useRef<HTMLElement>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const header = headerRef.current;
    const searchSection = searchSectionRef.current;

    if (!header || !searchSection) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry) {
          setIsCondensed(!entry.isIntersecting);
        }
      },
      {
        root: null,
        threshold: 0,
        rootMargin: `-${header.offsetHeight}px 0px 0px 0px`,
      },
    );

    observer.observe(searchSection);
    return () => observer.disconnect();
  }, []);

  // input text based on tab
  const tabText = isReviews ? 'Search address or building' : 'Start your apartment search';

  return (
    <>
      <header
        ref={headerRef}
        className={`sticky top-0 z-20 bg-primary pt-4 shadow-md transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] md:hidden ${
          isCondensed ? 'backdrop-blur-sm' : ''
        }`}
      >
        <div
          className={`flex flex-col gap-4 px-6 transition-all duration-500 ease-out ${
            isCondensed ? 'flex-row justify-between items-end' : 'items-center'
          }`}
        >
          <LogoLink variant={isCondensed ? 'vertical' : 'horizontal'} />
          {isCondensed ? (
            isReviews ? (
              <BuildingsSearchBarMobile className="w-full" eventScreen="home" />
            ) : aiEnabled ? (
              // AI mini search bar (#16): sparkle + text only (no search button per
              // Figma); tapping opens the full-page search overlay.
              <AiMiniSearchBar
                variant="mobile"
                className="w-full"
                onClick={() => setAiSearchOpen(true)}
              />
            ) : (
              <button
                onClick={() => {
                  if (searchSectionRef.current && headerRef.current) {
                    searchSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
                  }
                }}
                className="flex flex-1 items-center gap-2 px-4 py-3 animate-slide-up border border-secondary shadow rounded-full"
              >
                <IconSearch className="icon-tertiary size-4" />
                <span className="text-tertiary text-body-2">{tabText}</span>
              </button>
            )
          ) : (
            <h1
              className={`text-primary transition-all text-heading-6 duration-500 ease-out ${
                isCondensed ? 'text-left translate-y-[-2px]' : 'text-center'
              }`}
            >
              {h1}
            </h1>
          )}
        </div>

        {/* Tabs */}
        <div className="mt-4 px-6">
          <TabNavigation fullWidth />
        </div>
      </header>

      <div ref={searchSectionRef} className="py-6 bg-primary md:hidden">
        {isReviews ? (
          <BuildingsSearchBarMobile className="container-grid" eventScreen="home" />
        ) : (
          <>
            <HomeSearchMobile open={aiSearchOpen} onOpenChange={setAiSearchOpen} />
            {/* Renter-profile pill — self-hides once profile is
                completed. Kept off the reviews tab (renter-profile
                is rent-side only). */}
            <div className="flex justify-center mt-4">
              <PersonalizePill />
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default TopBarMobile;
